import {
  ArrowBack as ArrowBackIcon,
  WarningAmberOutlined as WarningAmberOutlinedIcon,
} from '@mui/icons-material';
import {
  Alert,
  alertClasses,
  Box,
  InputLabel,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import type {
  ActionFunctionArgs,
  AppLoadContext,
  MetaFunction,
} from '@remix-run/cloudflare';
import { data, redirect } from '@remix-run/cloudflare';
import {
  Form,
  useActionData,
  useNavigation,
  useSearchParams,
} from '@remix-run/react';
import type React from 'react';
import { Fragment } from 'react';
import { ulid } from 'ulid';

import { Mail } from '@tbd/life-jsx/SystemIcons';
import { COLORS } from '@tbd/life-tokens';
import { createLogger } from '@tbd/lift-portal-shared-config';
import { PrimaryButton } from '@tbd/lift-ui/components/PrimaryButton';
import { SecondaryButton } from '@tbd/lift-ui/components/SecondaryButton';
import { TextButton } from '@tbd/lift-ui/components/TextButton';

type PortalService = AppLoadContext['cloudflare']['env']['PORTAL_SERVICE'];

const routeLogger = createLogger([
  'lift-portal',
  'anonymous',
  'forgot-password',
]);

export const meta: MetaFunction = () => {
  return [{ title: 'LIFT - Reset password' }];
};

const CLOSE_ACTION = 'close';
const SEND_ACTION = 'send';
const RESEND_ACTION = 'resend';
const ACTION_NAME = '_action';

export const action = async ({ request, context }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const identifier = formData.get('identifier')?.toString();
  const returnToOrigin = request.headers.get('origin') ?? '';
  const actionName = formData.get(ACTION_NAME)?.toString();

  const { PORTAL_SERVICE } = context.cloudflare.env;
  switch (actionName) {
    case CLOSE_ACTION:
      return data({ error: undefined });

    case RESEND_ACTION:
      return await handleResendEmail({
        resetToken: formData.get('token')?.toString(),
        returnToOrigin,
        portalService: PORTAL_SERVICE,
      });

    case SEND_ACTION:
      return await handleSendEmail({
        email: identifier,
        returnToOrigin,
        portalService: PORTAL_SERVICE,
      });

    default:
      return data({ error: 'UNKNOWN_ACTION' });
  }
};

const FormAlert = ({ children }: { children: React.ReactNode }) => {
  const theme = useTheme();

  return (
    <Alert
      sx={{
        background: COLORS.warning_100,
        color: COLORS.warning_700,
        ...theme.typography['component-bold-s'],
        mt: theme.spacing(2),
        mb: theme.spacing(4),
        [`& .${alertClasses.icon}`]: {
          color: COLORS.warning_700,
          p: 0,
        },
        [`& .${alertClasses.action}`]: {
          p: 0,
        },
        [`& .${alertClasses.message}`]: {
          p: 0,
          overflow: 'unset',
        },
      }}
      icon={<WarningAmberOutlinedIcon />}
    >
      {children}
    </Alert>
  );
};

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const actionData = useActionData<typeof action>();
  const resetToken = searchParams.get('token');
  const isError = actionData && 'error' in actionData;
  const isParticipantError = isError && actionData.error === 'FORBIDDEN';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        paddingX: 3,
        paddingY: 4,
        backgroundColor: 'background.paper',
        borderRadius: '10px',
      }}
    >
      <Stack
        sx={{
          gap: 1,
        }}
      >
        {isParticipantError ? (
          <Fragment>
            <FormAlert>
              You are not able to reset your password. Please reach out to a
              facilitator for help signing in.
            </FormAlert>
            <ForgotPasswordEnterIdentifier />
          </Fragment>
        ) : resetToken ? (
          <Fragment>
            {isError ? <FormAlert>{actionData.error}</FormAlert> : null}
            <EmailSent token={resetToken} />
          </Fragment>
        ) : (
          <ForgotPasswordEnterIdentifier />
        )}
      </Stack>
    </Box>
  );
}

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
async function handleSendEmail(props: {
  email: string;
  portalService: PortalService;
  returnToOrigin: string;
}) {
  const logger = routeLogger.child('handleSendEmail');
  const { email, portalService, returnToOrigin } = props;
  if (!EMAIL_REGEX.test(email)) {
    return data({
      error: 'FORBIDDEN',
    });
  }

  const result = await portalService.generateResetTokenAndSendEmail({
    email,
    returnToOrigin,
  });
  logger.debug('generateResetTokenAndSendEmail result', result);
  if (result.success) {
    return redirect(`?token=${result.resetToken}`);
  } else {
    if (result.reason === 'MISSING_TARGET_USER') {
      logger.debug('missing target user', email);
      // make a real token so the user can't tell it didn't match,
      // but don't write to KV, or send email, etc
      return redirect(`?token=${ulid()}`);
    }
    return data({
      error: result.reason || 'UNKNOWN_ERROR',
    });
  }
}

async function handleResendEmail(props: {
  resetToken?: string;
  returnToOrigin: string;
  portalService: PortalService;
}) {
  const logger = routeLogger.child('handleResendEmail');
  const { portalService, resetToken, returnToOrigin } = props;
  if (!resetToken) {
    logger.debug('missing reset token');
    return data({
      error: 'MISSING_TOKEN',
    });
  }
  const resendResult = await portalService.resendResetEmail({
    resetToken,
    returnToOrigin,
  });
  logger.debug('resendResetEmail result', { resendResult });
  if (
    resendResult.success === true ||
    // likely because of a non-existing email
    // better to let them create a new one than indicate we know that email isn't a user
    resendResult.reason === 'TOKEN_NOT_FOUND'
  ) {
    return { success: true };
  } else {
    return data({
      error: resendResult.reason || 'UNKNOWN_ERROR',
    });
  }
}

function ForgotPasswordEnterIdentifier() {
  const theme = useTheme();
  const actionButtonsDisabled = useNavigation().state !== 'idle';
  return (
    <Fragment>
      <Stack
        sx={{
          alignItems: 'flex-start',
          flexDirection: 'column',
          paddingY: 1,
        }}
      >
        <TextButton
          text="Back to login"
          variant="tertiary"
          size="small"
          icon={<ArrowBackIcon />}
          href="/login"
        />
      </Stack>
      <Stack>
        <Typography
          component="h1"
          variant="component-bold-l"
          sx={{
            mb: theme.spacing(1),
          }}
        >
          Forgot your password?
        </Typography>

        <Typography
          variant="component-regular-m"
          sx={{
            mb: 1.5,
          }}
        >
          Enter the email linked to your LIFT account, and we&apos;ll send you a
          secure link to create a new password.
        </Typography>
      </Stack>
      <Form method="post" id="forgot-password" replace>
        <Stack>
          <InputLabel htmlFor="identifier">
            <Typography variant="component-regular-xxs">
              Email address
            </Typography>
          </InputLabel>
          <TextField
            required
            fullWidth
            id="identifier"
            name="identifier"
            sx={{ backgroundColor: 'white' }}
            autoComplete="username"
            autoFocus
            placeholder="e.g. annie@laerdal.com"
          />
        </Stack>
        <Stack
          sx={{
            marginTop: 2,
          }}
        >
          <Stack
            direction="column"
            sx={{
              alignItems: 'stretch',
            }}
          >
            <PrimaryButton
              text="Send email"
              disabled={actionButtonsDisabled}
              name={ACTION_NAME}
              value={SEND_ACTION}
              type="submit"
              form="forgot-password"
            />
          </Stack>
        </Stack>
      </Form>
    </Fragment>
  );
}

function EmailSent(props: { token: string }) {
  const theme = useTheme();
  const actionButtonsDisabled = useNavigation().state !== 'idle';
  return (
    <Fragment>
      <Stack
        sx={{
          alignItems: 'flex-start',
        }}
      >
        <IconInOutlinedCircle Icon={<Mail htmlColor={COLORS.white} />} />
      </Stack>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <Typography
          component="h1"
          variant="component-bold-l"
          sx={{
            mb: theme.spacing(1),
          }}
        >
          Check your email
        </Typography>

        <Typography
          variant="component-regular-s"
          color={COLORS.neutral_800}
          sx={{
            mb: 1.5,
          }}
        >
          If there is a user associated with this username or email, a reset
          link will be sent shortly.
        </Typography>
      </Stack>
      <Stack>
        <Form method="POST">
          <input type="hidden" name="token" value={props.token} />
          <SecondaryButton
            text="Resend email"
            sx={{ width: '100%' }}
            disabled={actionButtonsDisabled}
            name={ACTION_NAME}
            value={RESEND_ACTION}
            type="submit"
          />
        </Form>
      </Stack>
    </Fragment>
  );
}

// Can cleaned up and moved to life-ui later
function IconInOutlinedCircle(props: { Icon: React.ReactNode }) {
  const buttonSize = '44px';
  const gap = '10px';
  const borderWidth = '1px';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        p: '4px',
        borderRadius: `calc(${buttonSize} + ${gap} - (2 * ${borderWidth}))`,
        border: `${borderWidth} solid ${COLORS.accent1_200}`,
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          p: gap,
          flex: '0 0 auto',
          backgroundColor: COLORS.accent1_200,
          borderRadius: buttonSize,
        }}
      >
        {props.Icon}
      </Box>
    </Box>
  );
}
